<script setup lang="ts">
    import { CardMenu } from '@/components/ui/card';
    import { columns } from '@/tables/columns/company-groups';
    import { Head } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreateCompanyGroup from '@/components/company-groups/CreateCompanyGroup.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { BreadcrumbItem, NavItem } from '@/types';

    const { hasPermission } = usePermissions();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.settings',
        },
        {
            title: 'company-affiliation.navbar_header',
            href: route('v2.companies.index'),
        },
    ];

    const navItems: NavItem[] = [
        {
            title: 'companies.header',
            href: route('v2.companies.index'),
            permissionLevel: Permission.USERS_VIEW,
        },
        {
            title: 'company-affiliation.navbar_header',
            href: route('v2.company-groups.index'),
            permissionLevel: Permission.USERS_VIEW,
        },
    ];

    const { isPending, data } = useQuery({
        queryKey: [QueryKey.COMPANY_GROUPS],
        queryFn: getCompanyGroups,
    });

    async function getCompanyGroups() {
        const { data } = await axios.get(route('v2.company-groups.table'));
        return data.data;
    }
</script>

<template>
    <Head :title="$t('profile.profile_settings')" />

    <AppLayout :breadcrumbs="breadcrumbs">
        <PageCard>
            <DataTable
                :table-key="QueryKey.COMPANY_GROUPS"
                :columns="columns"
                :data="data"
                :loading="isPending"
            >
                <template #title>
                    <CardMenu :items="navItems" />
                </template>
                <template #actions>
                    <DataTableToolbarActions name="company-groups">
                        <CreateCompanyGroup v-if="hasPermission(Permission.COMPANIES_CREATE)" />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
